// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';

/**
 * Advisor access to the relationship Open Items panel (Panel 3). Proxied by
 * advisor-bff to the resource server at /api/profiles/{profileId}/open-items.
 * See docs/specs/relationship-summary-panels-prd.md.
 */

export type OpenItemStatus = 'OPEN' | 'COMPLETE' | 'DISMISSED';

export interface OpenItem {
  /** Null for platform-derived items (read-only). */
  id?: string | null;
  /** MANUAL | LLM | DERIVED */
  source: string;
  /** True for platform-derived items — read-only, auto-resolving. */
  derived: boolean;
  title: string;
  detail?: string | null;
  status: OpenItemStatus;
  dueDate?: string | null;
  completedAt?: string | null;
  createdAt?: string | null;
}

export const openItemsApi = {
  /** Merged manual + derived open items; null when the feature is disabled (404). */
  async list(profileId: string): Promise<OpenItem[] | null> {
    try {
      const { data } = await api.get<OpenItem[]>(`/profiles/${profileId}/open-items`);
      return data;
    } catch (e) {
      if ((e as { response?: { status?: number } })?.response?.status === 404) {
        return null;
      }
      throw e;
    }
  },

  /** Add a manual open item. */
  async add(profileId: string, title: string, detail?: string): Promise<OpenItem> {
    const { data } = await api.post<OpenItem>(`/profiles/${profileId}/open-items`, { title, detail });
    return data;
  },

  /** Mark a manual item complete or dismissed. */
  async updateStatus(profileId: string, itemId: string, status: OpenItemStatus): Promise<OpenItem> {
    const { data } = await api.patch<OpenItem>(`/profiles/${profileId}/open-items/${itemId}`, { status });
    return data;
  },
};
